import { useCallback, useState, type ReactNode } from 'react';
import { useAuth } from '../auth/useAuth';
import { useTimerState, type TimerResult } from '../../hooks/useTimerState';
import { TimerContext } from './TimerContext';

export function TimerProvider({ children }: { children: ReactNode }) {
  const { accountId } = useAuth();
  const { isRunning, isPaused, hasTimer, autoPaused, elapsed, timerState, start, pause, resume, stop, discard } = useTimerState(accountId ?? null);
  const [pendingTimerResult, setPendingTimerResult] = useState<TimerResult | null>(null);

  const startTimer = useCallback(
    (matterId: string, clientId: string, matterName: string, clientName: string) => {
      start(matterId, clientId, matterName, clientName);
    },
    [start],
  );

  const pauseTimer = useCallback(() => {
    pause();
  }, [pause]);

  const resumeTimer = useCallback(() => {
    resume();
  }, [resume]);

  const saveTimer = useCallback(() => {
    // Pause and create the pending result, but keep the timer alive.
    // If the user cancels the wizard, they can resume or save again.
    if (!timerState) return;
    if (!timerState.paused) pause();
    const currentElapsed = timerState.paused
      ? timerState.accumulatedSeconds
      : timerState.accumulatedSeconds + Math.floor((Date.now() - timerState.startedAt) / 1000);
    setPendingTimerResult({
      matterId: timerState.matterId,
      clientId: timerState.clientId,
      elapsedSeconds: currentElapsed,
    });
  }, [timerState, pause]);

  const stopTimer = useCallback(() => {
    // Permanently clear the timer (called after successful activity creation)
    stop();
  }, [stop]);

  const discardTimer = useCallback(() => {
    discard();
  }, [discard]);

  const clearPendingResult = useCallback(() => {
    setPendingTimerResult(null);
  }, []);

  return (
    <TimerContext
      value={{
        isRunning,
        isPaused,
        hasTimer,
        autoPaused,
        elapsed,
        matterId: timerState?.matterId ?? null,
        clientId: timerState?.clientId ?? null,
        matterName: timerState?.matterName ?? null,
        clientName: timerState?.clientName ?? null,
        startTimer,
        pauseTimer,
        resumeTimer,
        saveTimer,
        stopTimer,
        discardTimer,
        pendingTimerResult,
        clearPendingResult,
      }}
    >
      {children}
    </TimerContext>
  );
}
